import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'reactstrap';
import { Chip } from "@mui/material";
import { FatigueAssessmentAssign, FatigueAssessmentAssignResult, IUserSearchResult } from 'shared/models/api/models';
import { FormModal, FormModalSubHeader, FormModalPaddedBody, ModalButtonRow, ModalButton } from 'components/ui/FormModal';
import { fatigueAssessmentDashboardPathRoute } from 'components/portal/fatigueAssessment/FatigueAssessmentRoutes';
import { assign } from 'shared/services/fatigueAssessmentApi';
import InfoModal from 'components/ui/InfoModal';
import FatigueAssessmentPage from './FatigueAssessmentPage';
import { fatigueAssessmentAssign } from './FatigueAssessmentPageSelector';
import { handleActionChange } from './FatigueAssessmentActionState';
import { DateChoice, TimeChoice, UserChoice } from './FormControls';
import { isoDateStringToBrowserSafeString } from 'shared/models/mapping/mapping';

type ResultModalProps = {
    results?: FatigueAssessmentAssignResult,
    isOpen: boolean,
    closeAction: () => void
}

const ResultModal = (props: ResultModalProps) => {

    return (
        <FormModal {...props} headingText={`Assign Fatigue Assessments`}>

            <FormModalSubHeader>
                <div>{`Thank you for submitting the form`}</div>
            </FormModalSubHeader>

            <FormModalPaddedBody>
                <div className="modal-body-content-container">

                    <div className="modal-body-content">
                        <div>New fatigue assessments assigned: {props.results?.added || '0'}</div>
                        {!!props.results?.existing &&
                            <div>Requested users with existing pending assessments: {props.results?.existing}</div>
                        }
                        {!!props.results?.failed &&
                            <div>Assessments failed to assign: {props.results?.failed}</div>
                        }
                    </div>

                    <ModalButtonRow>
                        <ModalButton onClick={props.closeAction} text="OK" primaryButton={true} />
                    </ModalButtonRow>

                </div>
            </FormModalPaddedBody>
        </FormModal>
    );
}

interface UserChip {
    id: number;
    name: string;
}

const FatigueAssessmentAssignForm = () => {
    const [selectedUsers, setSelectedUsers] = useState([] as UserChip[]);
    const [shiftStartDate, setShiftStartDate] = useState("");
    const [shiftStartTime, setShiftStartTime] = useState("");

    const [validationEnabled, setValidationEnabled] = useState(false);
    const [displayAllRequired, setAllRequired] = useState(false);
    const [displayErrorSubmitting, setErrorSubmitting] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [displayResultModal, setResultModal] = useState(false);
    const [assignResults, setAssignResults] = useState({} as FatigueAssessmentAssignResult);

    const navigate = useNavigate();

    const handleUserSelected = (user: IUserSearchResult) => {
        if (!user || typeof (user.id) !== 'number') {
            return;
        }

        if (selectedUsers.findIndex(supervisor => supervisor.id === user.id) > -1) {
            removeUser(user.id);
            return;
        }

        setAllRequired(false);
        const newSupervisors = [...selectedUsers, { id: user.id, name: user.fullname } as UserChip];
        setSelectedUsers(newSupervisors);
        const newUserIds = newSupervisors.map(user => user.id);
        handleActionChange("assignAssessmentToUsers")(newUserIds);
    }
    const handleDueDateChange = (e: any) => {
        
        if (e.target?.value)
            setShiftStartDate(e.target?.value);
        else if (e)
            setShiftStartDate(e);
    }

    const handleTimeChange = (e: any) => {
        console.log(e);
        if (e.target?.value)
            setShiftStartTime(e.target?.value);
        else if (e)
            setShiftStartTime(e);
    }

    const removeUser = (userId: number) => {
        var index = selectedUsers.findIndex(supervisor => supervisor.id === userId);
        if (index > -1) {
            var newSupervisors = [...selectedUsers];
            newSupervisors.splice(index, 1);
            setSelectedUsers(newSupervisors);
            const newUserIds = newSupervisors.map(user => user.id);
            handleActionChange("assignAssessmentToUsers")(newUserIds);
        }
    }

    const clearUsers = () => {
        setValidationEnabled(false);
        setSelectedUsers([]);
        handleActionChange("assignAssessmentToUsers")([]);
    }

    const enableValidation = () => {
        setValidationEnabled(true);
    }

    const closeResults = () => {
        setResultModal(false);
        navigate(`${ fatigueAssessmentDashboardPathRoute.path }`);
    }

    const showResults = (results: FatigueAssessmentAssignResult) => {
        setAssignResults(results);
        setResultModal(true);
    }

    const errorResults = (e: any) => {
        if (e !== undefined) {
            console.error(e);
        }
        setIsSubmitting(false);
        setErrorSubmitting(true);
    }

    const submitResults = (results: any) => {
        setIsSubmitting(false);

        if (!results || results.failed > 0) {
            errorResults(undefined);
            return;
        }

        clearUsers();

        showResults(results);
    }


    const submitIfAble = async () => {
        let unansweredCount = 0;

        if (selectedUsers.length < 1) {
            unansweredCount++;
        }

        if (unansweredCount === 0) {
            const userIds = selectedUsers.map(u => u.id);

            let shiftStartLocalTimeString = isoDateStringToBrowserSafeString(`${ shiftStartDate }T${ shiftStartTime }:00`);
            let shiftStartLocalTime = new Date(shiftStartLocalTimeString);
            let faAssign = { userIds: userIds, shiftStartTime: shiftStartLocalTime } as FatigueAssessmentAssign;
            assign(faAssign)
                .then(submitResults)
                .catch(errorResults);
        }

        return unansweredCount;
    }

    const onSubmit = async () => {
        setIsSubmitting(true);
        enableValidation();

        let unansweredCount = 0;

        try {
            unansweredCount = await submitIfAble();
        }
        finally {
            if (unansweredCount > 0) {
                setAllRequired(true);
                setIsSubmitting(false);
            }
        }

    }
    let defaultShiftStartTime: Date = new Date(Date.now());
    return (
        <FatigueAssessmentPage selectedPage={fatigueAssessmentAssign}>
            <div className="fa-list-container fa-assign-form">

                <div className="main-form-header">Assign Fatigue Assessment to Users</div>

                <InfoModal text="There was an issue assigning the assessment(s). Please try again." isOpen={displayErrorSubmitting} closeAction={() => { setErrorSubmitting(false); }} />

                <ResultModal results={assignResults} isOpen={displayResultModal} closeAction={closeResults} />

                <div>

                    <div className="main-form-row" style={{ paddingBottom: 0 }}>
                        <div className="question">Users to assign a fatigue assessment</div>
                        <div className="answer">
                            <UserChoice
                                multichoice={true}
                                subscribeActionField="assignAssessmentToUsers"
                                invalid={validationEnabled && selectedUsers.length < 1}
                                onAnswerUpdate={handleUserSelected}
                                assign={true} />
                        </div>
                    </div>

                    <div className="row-divider" style={{ minHeight: "3rem" }}>
                        <div className="question" />
                        <div className="answer">
                            {selectedUsers.map((user) => (
                                <Chip key={user.id} className="m-1" label={user.name} onDelete={() => removeUser(user.id)} />
                            ))}
                        </div>
                    </div>

                    <div className="main-form-row row-divider">
                        <div className="question">Due Date</div>
                        <div className="answer">
                            <DateChoice date={defaultShiftStartTime} onAnswerUpdate={handleDueDateChange} />
                        </div>
                    </div>

                    <div className="main-form-row row-divider">
                        <div className="question">Due Time</div>
                        <div className="answer">
                            <TimeChoice time={defaultShiftStartTime} onAnswerUpdate={handleTimeChange} />
                        </div>
                    </div>



                </div>

                <div className="main-form-row form-end">
                    <div className={`invalid-feedback mandatory-details${ displayAllRequired === true ? " show" : "" }`}>Please enter all mandatory details in the form.</div>
                    <div className="submit">
                        {isSubmitting ?
                            <>
                                <div className="modal fade show" role="dialog" tabIndex={-1}></div>
                                <Button className="btn-primary disabled">Submitting...</Button>
                            </>
                            :
                            <>
                                <Button className="btn-primary" onClick={onSubmit}>Submit</Button>
                            </>
                        }
                    </div>
                </div>

            </div>
        </FatigueAssessmentPage>
    );
}

export default FatigueAssessmentAssignForm;
